import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const DEFAULT_DURATION_MS = 200;
const EASING = 'cubic-bezier(0.2, 0, 0, 1)';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement | null} */
  get content() {
    const content = this.details.querySelector(':scope > .details-content');

    return content instanceof HTMLElement ? content : null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #animatesHeight() {
    return this.dataset.animate === 'height';
  }

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {Animation | null} */
  #animation = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    this.#animation?.cancel();
  }

  /**
   * Whether the row is open, or opening. A row that is animating closed reports false.
   * @returns {boolean}
   */
  get isOpen() {
    return this.details.open && !this.details.classList.contains('details--closing');
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    const willOpen = !this.isOpen;

    if (willOpen) this.#closeSiblings();

    if (!this.#animatesHeight) return;

    event.preventDefault();
    this.#animateTo(willOpen);
  };

  open() {
    return this.#animateTo(true);
  }

  close() {
    return this.#animateTo(false);
  }

  toggle() {
    return this.#animateTo(!this.isOpen);
  }

  /**
   * Closes other open rows inside an auto-close accordion, keeping this row's header visible
   * when a row above it collapses.
   */
  #closeSiblings() {
    const parentAccordion = this.closest('[data-auto-close="true"]');

    if (!parentAccordion) return;

    /** @type {Promise<unknown>[]} */
    const collapsingAbove = [];

    for (const sibling of parentAccordion.querySelectorAll('accordion-custom')) {
      if (sibling === this || !(sibling instanceof AccordionCustom) || !sibling.isOpen) continue;

      const animation = sibling.close();
      const isAbove = Boolean(sibling.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING);

      if (animation && isAbove) collapsingAbove.push(animation.finished.catch(() => {}));
    }

    if (collapsingAbove.length) this.#keepSummaryInView(Promise.all(collapsingAbove));
  }

  /**
   * Leaves the page scroll alone while content above the summary collapses, then scrolls once
   * if the summary ended up above the viewport or under the sticky header.
   * Per-frame scroll compensation stutters on iOS and triggers the header's scroll-up reveal.
   * @param {Promise<unknown>} until - Resolves when the layout change is done.
   */
  async #keepSummaryInView(until) {
    const root = document.documentElement;

    // Without this, Chromium's scroll anchoring scrolls the page to compensate, unlike Safari.
    root.style.overflowAnchor = 'none';
    await until;
    root.style.removeProperty('overflow-anchor');

    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const offset = this.summary.getBoundingClientRect().top - headerHeight;

    if (offset >= 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollBy({ top: offset, behavior: reduceMotion ? 'instant' : 'smooth' });
  }

  /**
   * Animates the content height to open or closed. Interrupting a running animation continues
   * from the current height instead of jumping.
   * @param {boolean} shouldOpen - Target state.
   * @returns {Animation | null} The running animation, or null when the change is instant.
   */
  #animateTo(shouldOpen) {
    const { details, content } = this;

    if (shouldOpen === this.isOpen && !this.#animation) return null;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!content || !this.#animatesHeight || reduceMotion) {
      this.#animation?.cancel();
      details.open = shouldOpen;
      return null;
    }

    const current = getComputedStyle(content);
    const collapsed = { height: '0px', opacity: 0, paddingBlockStart: '0px', paddingBlockEnd: '0px' };
    const start = details.open
      ? {
          height: `${content.getBoundingClientRect().height}px`,
          opacity: Number(current.opacity),
          paddingBlockStart: current.paddingBlockStart,
          paddingBlockEnd: current.paddingBlockEnd,
        }
      : collapsed;

    this.#animation?.cancel();
    details.classList.toggle('details--closing', !shouldOpen);
    details.open = true;

    // Padding is animated with height: with border-box sizing, height 0 cannot shrink below it.
    const natural = getComputedStyle(content);
    const end = shouldOpen
      ? {
          height: `${content.getBoundingClientRect().height}px`,
          opacity: 1,
          paddingBlockStart: natural.paddingBlockStart,
          paddingBlockEnd: natural.paddingBlockEnd,
        }
      : collapsed;
    const animation = content.animate(
      [
        { ...start, overflow: 'clip' },
        { ...end, overflow: 'clip' },
      ],
      { duration: this.#duration(), easing: EASING, fill: 'forwards' }
    );

    this.#animation = animation;

    animation.addEventListener('finish', () => {
      if (this.#animation !== animation) return;

      this.#animation = null;
      details.classList.remove('details--closing');
      details.open = shouldOpen;
      animation.cancel();
    });

    animation.addEventListener('cancel', () => {
      if (this.#animation === animation) {
        this.#animation = null;
        details.classList.remove('details--closing');
      }
    });

    return animation;
  }

  /**
   * @returns {number} Animation duration in milliseconds, from the theme speed token.
   */
  #duration() {
    const value = getComputedStyle(this).getPropertyValue('--animation-speed-slow').trim();
    const amount = parseFloat(value);

    if (Number.isNaN(amount)) return DEFAULT_DURATION_MS;

    return value.endsWith('ms') ? amount : amount * 1000;
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.#animation?.cancel();
    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.#animation?.cancel();
      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
